import React, { type JSX, ReactNode, useContext, useMemo } from 'react';

import { ButtonVariant, LinkAsButton } from '@/shared/ui';

import { UnavailabilityModal } from '@/components/Forms/ServiceUnAvailable/UnavailabilityModal';
import { UnavailabilityPage } from '@/components/Forms/ServiceUnAvailable/UnavailabilityPage';

import { OTHER_ROUTES, ROUTES, ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { FeatureContext } from '@/contexts/Features/FeatureContext';
import { Reason } from '@/contexts/ServiceAvailability/ServiceAvailabilityContext';

import { SERVICE_CODES } from '@/lib/consts';

import { ERZServiceLockedReason, FeatureName } from '@/api/types';

import { LinkAsButton as LinkAsButtonOld } from '../../Button';

import {
  getBIAReasonedMessage,
  getERZReasonedMessage,
  getCommonMsg,
  getIACReasonedMessage,
  getRegisteredAddressAbroadGeneralMessage,
  getServiceUnAvailableMessage,
  getTRRCancelationMsg,
  getTRRMsg,
} from './Messages';

import './ServiceUnAvailable.scss';

const buttons = {
  home: <LinkAsButton to={ROUTES.HOME}>На головну</LinkAsButton>,
  homeSecondary: (
    <LinkAsButton to={ROUTES.HOME} variant={ButtonVariant.SECONDARY}>
      На головну
    </LinkAsButton>
  ),
  homeOld: (
    <LinkAsButtonOld to={ROUTES.HOME} isFormButton position="right-side">
      На головну
    </LinkAsButtonOld>
  ),
  weaponRegistered: (
    <LinkAsButtonOld to={OTHER_ROUTES.erz.WEAPON_REGISTERED} outline isFormButton>
      До моєї зброї
    </LinkAsButtonOld>
  ),
  userRequests: (
    <LinkAsButtonOld to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS} outline isFormButton>
      Мої запити
    </LinkAsButtonOld>
  ),
  profile: <LinkAsButton to={ROUTES.USER.PROFILE}>Особистий кабінет</LinkAsButton>,
  profileOld: (
    <LinkAsButtonOld to={ROUTES.USER.PROFILE} isFormButton>
      Особистий кабінет
    </LinkAsButtonOld>
  ),
};

export enum ServiceUnavailableView {
  PAGE = 'page',
  MODAL = 'modal',
}

interface Props {
  causedBy: Nullable<FeatureName | Reason>;
  customMsg: Nullable<JSX.Element>;
  view: ServiceUnavailableView;
}

export type UnavailabilityContent = {
  title?: string;
  message: ReactNode;
};

export const ServiceUnAvailable: React.FC<Props> = ({
  causedBy,
  customMsg = null,
  view = ServiceUnavailableView.PAGE,
}) => {
  const { getFeatureDetails } = useContext(FeatureContext);

  const content = useMemo<UnavailabilityContent>(() => {
    switch (causedBy) {
      case Reason.NOT_ENOUGH_DATA:
        return getCommonMsg();

      case Reason.REGISTERED_ADDRESS_ABROAD:
        return getRegisteredAddressAbroadGeneralMessage();

      case Reason.SERVICE_NOT_AVAILABLE:
        return getServiceUnAvailableMessage();

      case FeatureName.IAC_SERVICES:
        return getIACReasonedMessage(getFeatureDetails(causedBy));

      case FeatureName.BIA_SERVICES:
        return getBIAReasonedMessage(getFeatureDetails(causedBy));

      case FeatureName.ERZ_REGISTRY:
        return getCommonMsg();

      case FeatureName.ERZ_SERVICES:
        return getERZReasonedMessage(getFeatureDetails(causedBy));

      case FeatureName.ERZ_TEMPORARY_RESIDENCE_RELOCATION:
        return getTRRMsg(getFeatureDetails(causedBy));

      case FeatureName.ERZ_CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION:
        return getTRRCancelationMsg(getFeatureDetails(causedBy));

      default:
        return { message: <React.Fragment /> };
    }
  }, [causedBy, getFeatureDetails]);

  const actionButtons = useMemo<JSX.Element>(() => {
    switch (causedBy) {
      case Reason.NOT_ENOUGH_DATA:
      case Reason.DUPLICATED_APPLICATION:
      case Reason.MIN_USER_AGE:
      case Reason.REGISTERED_ADDRESS_ABROAD:
        return buttons.homeOld;

      case FeatureName.IAC_SERVICES:
        const reason = getFeatureDetails(causedBy);

        switch (reason) {
          case Reason.MIN_USER_AGE:
          case Reason.UNAVAILABLE_MIN_USER_AGE:
            return (
              <>
                {buttons.profile}
                {buttons.homeSecondary}
              </>
            );

          default:
            return buttons.home;
        }

      case Reason.SERVICE_NOT_AVAILABLE:
        return (
          <>
            {buttons.userRequests}
            {buttons.homeOld}
          </>
        );

      case FeatureName.BIA_SERVICES: {
        const reason = getFeatureDetails(causedBy);

        switch (reason) {
          case Reason.SERVICE_NOT_AVAILABLE:
            return (
              <>
                {buttons.userRequests}
                {buttons.homeOld}
              </>
            );

          default:
            return buttons.homeOld;
        }
      }

      case FeatureName.ERZ_TEMPORARY_RESIDENCE_RELOCATION: {
        const reason = getFeatureDetails(causedBy);
        return reason === Reason.DUPLICATED_APPLICATION ? (
          buttons.homeOld
        ) : (
          <LinkAsButtonOld
            to={ROUTES_BY_SERVICE_CODE[SERVICE_CODES.CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION]}
            isFormButton
            position="centered"
          >
            Скасувати
          </LinkAsButtonOld>
        );
      }

      case FeatureName.ERZ_SERVICES:
        return (
          <>
            {getFeatureDetails(causedBy) !== ERZServiceLockedReason.NO_CITIZENSHIP_OR_RESIDENCE_DOCUMENT &&
              buttons.weaponRegistered}
            {buttons.homeOld}
          </>
        );

      default:
        return buttons.homeOld;
    }
  }, [causedBy, getFeatureDetails]);

  if (customMsg) {
    return <UnavailabilityPage message={customMsg} actionButtons={actionButtons} />;
  }

  return view === ServiceUnavailableView.MODAL ? (
    <UnavailabilityModal title={content.title} message={content.message} actionButtons={actionButtons} />
  ) : (
    <UnavailabilityPage title={content.title} message={content.message} actionButtons={actionButtons} />
  );
};
